import { iconImages, logoImages, utilsImages } from '@/assets/images'
import CookieConsent from '@/components/cookie'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { PageLoader } from '@/components/ui/loader'
import { useTheme } from '@/hooks/use-theme'
import { cn } from '@/lib/utils'
import {
  identifierSchema,
  passwordOnlySchema,
  type IdentifierSchemaType,
  type PasswordOnlySchemaType,
} from '@/schema/auth/login'
import { handleEnterpriseSSO, isEnterpriseSSOStrategy, isTransferableSSO, transferSSOToSignUp } from '@/utils/oauth'
import { scrollToTop } from '@/utils/scroll-to-top'
import {
  EMAIL_CODE_FACTOR,
  ENTERPRISE_SSO_FACTOR,
  getEmailCodeFactor,
  hasFactor,
  hasNoSupportedFactor,
  isEnterpriseSSOOnly,
  PASSKEY_FACTOR,
  PASSWORD_FACTOR,
  type SignInFactors,
} from '@/utils/sign-in-factors'
import { useAuth, useSignIn, useSignUp } from '@clerk/clerk-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Building2, KeyRound, Loader2, Mail, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

const successMessage = 'Welcome to CISO Adapt!'

const VENDOR_INVITATION_TOKEN_KEY = 'vendorInvitationToken'

const mfaPrompt = {
  title: 'Two-factor authentication required',
  description: 'Please enter the code from your authenticator app or a backup code',
} as const

/**
 * The form walks three steps: collect the email, offer only the methods Clerk
 * says that email may use, then MFA if the chosen method asks for it.
 */
type SignInStep = 'identifier' | 'methods' | 'mfa'

/** The parts of a Clerk sign-in attempt this form acts on. */
interface SignInOutcome {
  status?: string | null
  createdSessionId?: string | null
}

/**
 * A pending vendor invitation must be claimed after sign-in (links the vendor to
 * the supply chain's assessment), so route through the portal claim page;
 * everyone else lands on the dashboard root.
 */
const getPostLoginPath = (): string => (localStorage.getItem(VENDOR_INVITATION_TOKEN_KEY) ? '/portal/welcome' : '/')

export function SigninForm({ className, ...props }: React.ComponentProps<'div'>) {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signIn, isLoaded, setActive } = useSignIn()
  const { signUp } = useSignUp()
  const { isSignedIn } = useAuth()

  const [step, setStep] = useState<SignInStep>('identifier')
  const [identifier, setIdentifier] = useState('')
  const [factors, setFactors] = useState<SignInFactors>(null)

  const [emailCodeSent, setEmailCodeSent] = useState(false)
  const [emailCode, setEmailCode] = useState('')
  const [pendingMethod, setPendingMethod] = useState<string | null>(null)

  const [mfaCode, setMfaCode] = useState('')
  const [isVerifyingMFA, setIsVerifyingMFA] = useState(false)
  const [mfaMode, setMfaMode] = useState<'totp' | 'backup'>('totp')
  const mfaCodeLength = mfaMode === 'totp' ? 6 : 8

  const identifierForm = useForm<IdentifierSchemaType>({ resolver: zodResolver(identifierSchema) })
  const passwordForm = useForm<PasswordOnlySchemaType>({ resolver: zodResolver(passwordOnlySchema) })

  const canUsePassword = hasFactor(factors, PASSWORD_FACTOR)
  const canUseEmailCode = Boolean(getEmailCodeFactor(factors)?.emailAddressId)
  const canUsePasskey = hasFactor(factors, PASSKEY_FACTOR)
  const canUseSSO = hasFactor(factors, ENTERPRISE_SSO_FACTOR)

  /** Activates the session, or moves to the MFA step. Returns false if neither applied. */
  const finishSignIn = async (result?: SignInOutcome | null): Promise<boolean> => {
    if (result?.status === 'complete' && setActive) {
      // do not use navigate here, it will cause the page to reload and the user will be logged out
      // use setActive instead
      await setActive({ session: result.createdSessionId, redirectUrl: '/' })
      toast.success(successMessage)
      return true
    }

    if (result?.status === 'needs_second_factor') {
      setStep('mfa')
      toast.info(mfaPrompt.title, { description: mfaPrompt.description })
      return true
    }

    return false
  }

  // Step 1: ask Clerk what this email is allowed to use. The answer decides
  // which controls step 2 renders, so an SSO-only domain never sees a password
  // box it cannot use, and vice versa.
  const onIdentifierSubmit = async ({ email }: IdentifierSchemaType) => {
    try {
      if (!isLoaded) return

      const result = await signIn?.create({ identifier: email })
      const supportedFactors = result?.supportedFirstFactors ?? []

      setIdentifier(email)
      setFactors(supportedFactors)

      // Nothing to choose between when SSO is the only option, so go straight to
      // the IdP. Only fall through to the method step if the redirect did not happen.
      if (isEnterpriseSSOOnly(supportedFactors)) {
        const outcome = await startSSO(email)
        if (outcome === 'redirecting' || outcome === 'complete') return
      }

      setStep('methods')

      // A sign-in can already be finished at this point (e.g. an active ticket).
      await finishSignIn(result)
    } catch (err) {
      const code = (err as { errors?: { code?: string }[] })?.errors?.[0]?.code

      if (code === 'session_exists') {
        navigate(getPostLoginPath(), { replace: true })
        toast.success(successMessage)
        return
      }

      if (code === 'form_identifier_not_found') {
        identifierForm.setError('email', { message: 'No account found for this email address' })
        return
      }

      toast.error('Could not continue', {
        description: err instanceof Error ? err.message : 'Please check your email address and try again',
      })
    }
  }

  const backToIdentifier = () => {
    setStep('identifier')
    setFactors(null)
    setEmailCodeSent(false)
    setEmailCode('')
    setPendingMethod(null)
    passwordForm.reset()
  }

  const onPasswordSubmit = async ({ password }: PasswordOnlySchemaType) => {
    try {
      if (!isLoaded) return

      const result = await signIn?.attemptFirstFactor({ strategy: 'password', password })
      if (!(await finishSignIn(result))) console.log('Sign-in not complete:', result)
    } catch (err) {
      passwordForm.setError('password', {
        message: err instanceof Error ? err.message : 'Please check your password and try again',
      })
    }
  }

  const sendEmailCode = async () => {
    const emailAddressId = getEmailCodeFactor(factors)?.emailAddressId
    if (!signIn || !emailAddressId) return

    setPendingMethod(EMAIL_CODE_FACTOR)
    try {
      await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId })
      setEmailCodeSent(true)
      toast.info('Code sent', { description: `We emailed a sign-in code to ${identifier}` })
    } catch (err) {
      toast.error('Could not send the code', {
        description: err instanceof Error ? err.message : 'Please try another sign-in method',
      })
    } finally {
      setPendingMethod(null)
    }
  }

  const verifyEmailCode = async () => {
    if (!signIn || !emailCode.trim()) return

    setPendingMethod('email_code_verify')
    try {
      const result = await signIn.attemptFirstFactor({ strategy: 'email_code', code: emailCode })
      if (!(await finishSignIn(result)))
        toast.error('Invalid code', { description: 'Please check the code and try again' })
    } catch (err) {
      toast.error('Invalid code', {
        description: err instanceof Error ? err.message : 'The code is invalid or has expired',
      })
    } finally {
      setPendingMethod(null)
    }
  }

  const signInWithPasskey = async () => {
    if (!signIn) return

    setPendingMethod(PASSKEY_FACTOR)
    try {
      const result = await signIn.authenticateWithPasskey()
      if (!(await finishSignIn(result)))
        toast.error('Passkey sign-in failed', { description: 'Please try another sign-in method' })
    } catch (err) {
      toast.error('Passkey sign-in failed', {
        description: err instanceof Error ? err.message : 'Please try another sign-in method',
      })
    } finally {
      setPendingMethod(null)
    }
  }

  const startSSO = async (email = identifier) => {
    setPendingMethod(ENTERPRISE_SSO_FACTOR)
    const outcome = await handleEnterpriseSSO({ mode: 'signin', emailAddress: email, signIn, isLoaded })
    setPendingMethod(null)
    return outcome
  }

  const handleMFAVerification = async () => {
    if (!signIn || !mfaCode.trim()) {
      toast.error('Please enter the verification code')
      return
    }

    setIsVerifyingMFA(true)
    try {
      // A 6-digit code is from the authenticator app; anything else is a backup code.
      const result =
        mfaCode.length === 6
          ? await signIn.attemptSecondFactor({ strategy: 'totp', code: mfaCode })
          : await signIn.attemptSecondFactor({ strategy: 'backup_code', code: mfaCode })

      if (result?.status === 'complete') {
        await setActive({ session: result.createdSessionId, redirectUrl: '/' })
        toast.success(successMessage)
        setStep('methods')
        setMfaCode('')
      } else {
        toast.error('Invalid code', { description: 'Please check the code and try again' })
      }
    } catch (error) {
      console.error('MFA verification failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Verification failed'

      if (errorMessage.includes('invalid') || errorMessage.includes('expired')) {
        toast.error('Invalid code', {
          description: 'The code you entered is invalid or expired. Please try again.',
        })
      } else {
        toast.error('Verification failed', { description: errorMessage })
      }
    } finally {
      setIsVerifyingMFA(false)
    }
  }

  // Capture a pending vendor invitation token from the login link (existing users are
  // emailed /sign-in?invitationToken=...). Persisting it routes them to /portal/welcome
  // after sign-in, where the invitation is claimed. Runs before the redirect effect below.
  useEffect(() => {
    const invitationToken = searchParams.get('invitationToken')
    if (invitationToken) localStorage.setItem(VENDOR_INVITATION_TOKEN_KEY, invitationToken)
  }, [searchParams])

  useEffect(() => {
    // if user is signed in, navigate to home page (or the vendor claim page when a
    // vendor invitation is pending, so the invitation gets claimed/linked)
    if (isSignedIn) navigate(getPostLoginPath(), { replace: true })
  }, [isSignedIn, navigate])

  // Handle the enterprise SSO callback. The IdP redirect lands back here with the
  // verification already settled, but the sign-in itself still sitting at
  // "needs_first_factor"/"needs_second_factor", so it has to be finished off:
  // provisioned via sign-up when transferable, or activated when verified.
  useEffect(() => {
    const handleSSOCallback = async () => {
      if (!isLoaded || !signIn || isSignedIn) return

      const firstFactor = signIn.firstFactorVerification
      if (!isEnterpriseSSOStrategy(firstFactor?.strategy)) return

      // The IdP authenticated the user but Clerk has no account for them yet, so
      // hand the attempt over to a sign-up to provision one.
      if (isTransferableSSO(firstFactor)) {
        const createdSessionId = await transferSSOToSignUp(signUp)
        if (!createdSessionId) return

        await setActive({ session: createdSessionId, redirectUrl: '/' })
        toast.success(successMessage)
        navigate('/sign-in', { replace: true })
        return
      }

      if (firstFactor?.status !== 'verified') return

      if (signIn.status === 'needs_second_factor') {
        setStep('mfa')
        toast.info(mfaPrompt.title, { description: mfaPrompt.description })
        return
      }

      if (signIn.status !== 'needs_first_factor') return

      try {
        // If session already exists, just activate it
        if (signIn.createdSessionId) {
          await setActive({ session: signIn.createdSessionId, redirectUrl: '/' })
          toast.success(successMessage)
          navigate('/sign-in', { replace: true })
          return
        }

        // Reload sign-in to get latest state (Clerk might have created session)
        await signIn.reload()

        if (signIn.createdSessionId) {
          await setActive({ session: signIn.createdSessionId, redirectUrl: '/' })
          toast.success(successMessage)
          navigate('/sign-in', { replace: true })
          return
        }

        // Store status in variable to get fresh type after reload
        const statusAfterReload: string = signIn.status as string
        if (statusAfterReload === 'needs_second_factor') {
          setStep('mfa')
          toast.info(mfaPrompt.title, { description: mfaPrompt.description })
          return
        }

        // If still no session, try to complete the sign-in
        // For a verified SSO factor, we need to "attempt" the first factor to finalize
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (signIn.attemptFirstFactor as any)({ strategy: firstFactor.strategy })

        if (result?.status === 'complete' && result.createdSessionId) {
          await setActive({ session: result.createdSessionId, redirectUrl: '/' })
          toast.success(successMessage)
          navigate('/sign-in', { replace: true })
        } else if (result?.status === 'needs_second_factor') {
          setStep('mfa')
          toast.info(mfaPrompt.title, { description: mfaPrompt.description })
        }
      } catch (err) {
        console.error('Enterprise SSO callback completion error:', err)
        // Don't show error immediately - might be a timing issue
        // The user can retry if needed
      }
    }

    // Small delay to ensure Clerk has processed the SSO callback
    const timeoutId = setTimeout(handleSSOCallback, 200)
    return () => clearTimeout(timeoutId)
  }, [isLoaded, signIn, signUp, isSignedIn, setActive, navigate])

  if (!isLoaded) return <PageLoader />

  return (
    <>
      <div className={cn('flex  flex-col gap-6  xl:w-[60%] lg:w-[80%] md:w-full w-full', className)} {...props}>
        <Card className='overflow-hidden p-0 shadow-md'>
          <CardContent className='grid p-0 md:grid-cols-2'>
            <div className='p-6 md:p-8'>
              <div className='flex flex-col gap-4'>
                <div className='flex flex-col items-center text-center'>
                  <img
                    src={theme === 'dark' ? logoImages.logoLight : logoImages.logoBlack}
                    alt='Image'
                    loading='lazy'
                    decoding='async'
                    className='h-12 w-auto'
                  />
                  <h1 className='text-2xl font-medium pt-3'>Welcome Back</h1>
                  <p className='text-muted-foreground text-sm'>
                    {step === 'identifier'
                      ? 'Enter your email to continue'
                      : step === 'methods'
                        ? 'Choose how you want to sign in'
                        : 'Confirm it is you to finish signing in'}
                  </p>
                </div>

                {/* STEP 1 - identifier only. The available methods are unknown until Clerk is asked. */}
                {step === 'identifier' && (
                  <form
                    onSubmit={identifierForm.handleSubmit(onIdentifierSubmit, () => scrollToTop())}
                    className='flex flex-col gap-4'
                  >
                    <div className='grid gap-3'>
                      <Input
                        id='email'
                        type='email'
                        autoComplete='email'
                        placeholder='Enter your email (eg, john@example.com)'
                        required
                        {...identifierForm.register('email')}
                        error={identifierForm.formState.errors.email?.message}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            identifierForm.handleSubmit(onIdentifierSubmit, () => scrollToTop())()
                          }
                        }}
                      />
                    </div>

                    <Button type='submit' className='w-full' disabled={identifierForm.formState.isSubmitting}>
                      {identifierForm.formState.isSubmitting ? (
                        <>
                          <Loader2 className='w-4 h-4 animate-spin' />
                          <span>Checking...</span>
                        </>
                      ) : (
                        'Continue'
                      )}
                    </Button>

                    <div className='after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t'>
                      <span className='bg-card text-muted-foreground relative z-10 px-2'>Or continue with</span>
                    </div>
                    <div className='grid grid-cols-2 gap-4'>
                      <Button variant='outline' type='button' className='w-full' disabled>
                        <img
                          loading='lazy'
                          decoding='async'
                          src={iconImages.appleIcon}
                          alt='Apple'
                          className='w-4 h-4'
                        />
                        <span className='sr-only'>Login with Apple</span>
                      </Button>
                      <Button variant='outline' type='button' className='w-full' disabled>
                        <img
                          loading='lazy'
                          decoding='async'
                          src={iconImages.googleIcon}
                          alt='Google'
                          className='w-4 h-4'
                        />
                        <span className='sr-only'>Login with Google</span>
                      </Button>
                    </div>
                  </form>
                )}

                {/* STEP 2 - only the methods Clerk reported for this identifier. */}
                {step === 'methods' && (
                  <div className='flex flex-col gap-4'>
                    <div className='flex items-center justify-between gap-2 rounded-md border px-3 py-2'>
                      <span className='text-sm truncate'>{identifier}</span>
                      <button
                        type='button'
                        onClick={backToIdentifier}
                        className='text-xs text-blue-400 hover:underline cursor-pointer shrink-0 flex items-center gap-1'
                      >
                        <ArrowLeft className='h-3 w-3' />
                        Change
                      </button>
                    </div>

                    {canUseSSO && (
                      <Button
                        type='button'
                        variant={canUsePassword ? 'outline' : 'default'}
                        className='w-full'
                        onClick={() => startSSO()}
                        disabled={pendingMethod === ENTERPRISE_SSO_FACTOR}
                      >
                        {pendingMethod === ENTERPRISE_SSO_FACTOR ? (
                          <>
                            <Loader2 className='w-4 h-4 animate-spin' />
                            <span>Redirecting...</span>
                          </>
                        ) : (
                          <>
                            <Building2 className='w-4 h-4' />
                            <span>Continue with single sign-on</span>
                          </>
                        )}
                      </Button>
                    )}

                    {canUsePassword && !(canUseEmailCode && emailCodeSent) && (
                      <form
                        onSubmit={passwordForm.handleSubmit(onPasswordSubmit, () => scrollToTop())}
                        className='flex flex-col gap-3'
                      >
                        <Input
                          id='password'
                          type='password'
                          autoComplete='current-password'
                          placeholder='Enter your password'
                          required
                          {...passwordForm.register('password')}
                          error={passwordForm.formState.errors.password?.message}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              passwordForm.handleSubmit(onPasswordSubmit, () => scrollToTop())()
                            }
                          }}
                        />
                        <Button type='submit' className='w-full' disabled={passwordForm.formState.isSubmitting}>
                          {passwordForm.formState.isSubmitting ? (
                            <>
                              <Loader2 className='w-4 h-4 animate-spin' />
                              <span>Signing in...</span>
                            </>
                          ) : (
                            'Sign In'
                          )}
                        </Button>

                        <Link to='/forget-password' className='ml-auto text-sm underline-offset-2 hover:underline'>
                          Forgot your password?
                        </Link>
                      </form>
                    )}

                    {canUsePasskey && (
                      <Button
                        type='button'
                        variant='outline'
                        className='w-full'
                        onClick={signInWithPasskey}
                        disabled={pendingMethod === PASSKEY_FACTOR}
                      >
                        {pendingMethod === PASSKEY_FACTOR ? (
                          <Loader2 className='w-4 h-4 animate-spin' />
                        ) : (
                          <KeyRound className='w-4 h-4' />
                        )}
                        <span>Use a passkey</span>
                      </Button>
                    )}

                    {canUseEmailCode && !emailCodeSent && (
                      <Button
                        type='button'
                        variant='outline'
                        className='w-full'
                        onClick={sendEmailCode}
                        disabled={pendingMethod === EMAIL_CODE_FACTOR}
                      >
                        {pendingMethod === EMAIL_CODE_FACTOR ? (
                          <Loader2 className='w-4 h-4 animate-spin' />
                        ) : (
                          <Mail className='w-4 h-4' />
                        )}
                        <span>Email me a sign-in code</span>
                      </Button>
                    )}

                    {canUseEmailCode && emailCodeSent && (
                      <div className='space-y-3'>
                        <p className='text-xs text-muted-foreground'>Enter the 6-digit code we emailed you</p>
                        <InputOTP maxLength={6} value={emailCode} onChange={setEmailCode}>
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                        <Button
                          type='button'
                          className='w-full'
                          onClick={verifyEmailCode}
                          disabled={pendingMethod === 'email_code_verify' || !emailCode.trim()}
                        >
                          {pendingMethod === 'email_code_verify' ? (
                            <>
                              <Loader2 className='w-4 h-4 animate-spin' />
                              <span>Verifying...</span>
                            </>
                          ) : (
                            'Verify code'
                          )}
                        </Button>
                        <button
                          type='button'
                          onClick={sendEmailCode}
                          className='text-xs text-blue-400 hover:underline cursor-pointer'
                        >
                          Resend code
                        </button>
                      </div>
                    )}

                    {hasNoSupportedFactor(factors) && (
                      <p className='text-sm text-muted-foreground'>
                        No sign-in method is available for this account here. Please contact your administrator.
                      </p>
                    )}
                  </div>
                )}

                {/* STEP 3 - second factor, reached from whichever method asked for it. */}
                {step === 'mfa' && (
                  <div className='space-y-3 mt-5'>
                    <div className='flex items-center gap-2'>
                      <ShieldCheck className='h-4 w-4 text-primary' />
                      <p className='text-sm font-medium'>Two-Factor Authentication</p>
                    </div>
                    <div className={cn('flex gap-2', mfaMode === 'backup' && 'gap-1.5')}>
                      <InputOTP maxLength={mfaCodeLength} value={mfaCode} onChange={(value) => setMfaCode(value)}>
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                          {mfaMode === 'backup' && (
                            <>
                              <InputOTPSlot index={6} />
                              <InputOTPSlot index={7} />
                            </>
                          )}
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    <div className='space-y-1'>
                      <p className='text-xs text-muted-foreground'>
                        {mfaMode === 'totp'
                          ? 'Enter the 6-digit code from your authenticator app'
                          : 'Enter your 8-character backup code'}
                      </p>

                      <p
                        onClick={() => setMfaMode(mfaMode === 'totp' ? 'backup' : 'totp')}
                        className='text-xs  cursor-pointer hover:underline text-blue-400'
                      >
                        {mfaMode === 'totp' ? 'Use Recovery Code' : 'Use Authenticator Code'}
                      </p>
                    </div>

                    <Button
                      type='button'
                      className='w-full'
                      onClick={() => handleMFAVerification()}
                      disabled={isVerifyingMFA || !mfaCode.trim()}
                    >
                      {isVerifyingMFA ? (
                        <>
                          <Loader2 className='w-4 h-4 animate-spin' />
                          <span>Verifying...</span>
                        </>
                      ) : (
                        'Verify'
                      )}
                    </Button>
                  </div>
                )}

                <div className='text-center text-sm'>
                  Don&apos;t have an account?&nbsp;
                  <Link to='/sign-up' className='underline underline-offset-4'>
                    Sign up
                  </Link>
                </div>
              </div>
            </div>
            <div className='relative hidden md:block'>
              <img
                loading='lazy'
                decoding='async'
                src={utilsImages.loginPageSide}
                alt='Image'
                className='absolute inset-0 h-full w-full object-cover   dark:grayscale'
              />
            </div>
          </CardContent>
        </Card>
        <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
          By clicking continue, you agree to our <Link to='/terms-of-service'>Terms of Service</Link> and&nbsp;
          <Link to='/privacy-policy'>Privacy Policy</Link>.
        </div>
      </div>
      <CookieConsent variant='small' />
    </>
  )
}
